import { z } from 'zod';

/** Shared bounded counter limit for the staff runtime-observability contract. */
export const ADMIN_RUNTIME_OBSERVABILITY_MAX_COUNTER = 2_000_000_000;
export const ADMIN_RUNTIME_HTTP_ROUTE_LIMIT = 96;
export const ADMIN_RUNTIME_HTTP_TOP_ROUTE_COUNT = 3;

export const ADMIN_HTTP_RUNTIME_STATUS_CLASSES = [
  '1xx',
  '2xx',
  '3xx',
  '4xx',
  '5xx',
  'aborted',
] as const;

const BoundedRuntimeIntegerSchema = z.number()
  .int()
  .nonnegative()
  .max(ADMIN_RUNTIME_OBSERVABILITY_MAX_COUNTER);

export const HttpRuntimeStatusClassesSchema = z.object(
  Object.fromEntries(
    ADMIN_HTTP_RUNTIME_STATUS_CLASSES.map((statusClass) => [statusClass, BoundedRuntimeIntegerSchema]),
  ) as Record<(typeof ADMIN_HTTP_RUNTIME_STATUS_CLASSES)[number], typeof BoundedRuntimeIntegerSchema>,
).strict();

export const HttpRuntimeRoutePatternSchema = z.string()
  .min(1)
  .max(ADMIN_RUNTIME_HTTP_ROUTE_LIMIT)
  .regex(/^[A-Z]+ \/api(?:\/|$)/, 'HTTP route must be a method and /api pattern')
  .refine((route) => !/[?#]/.test(route), 'HTTP route must not include query or fragment text')
  // Express exposes `:caseId`, while an accidental use of req.url would carry
  // the concrete UUID. Reject it at the Redis/API boundary as a second guard.
  .refine(
    (route) => !/\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:\/|$)/i.test(route),
    'HTTP route must not include a concrete UUID segment',
  );

export const HttpRuntimeCompletedSchema = z.object({
  count: BoundedRuntimeIntegerSchema,
  totalDurationMs: BoundedRuntimeIntegerSchema,
  maxDurationMs: BoundedRuntimeIntegerSchema,
  statusClasses: HttpRuntimeStatusClassesSchema,
}).strict().superRefine((completed, context) => {
  const statusCount = Object.values(completed.statusClasses).reduce((sum, count) => sum + count, 0);
  if (statusCount !== completed.count || completed.maxDurationMs > completed.totalDurationMs) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `HTTP completed invariant failed: statusClasses total ${statusCount} must equal count ${completed.count}; maxDurationMs ${completed.maxDurationMs} must not exceed totalDurationMs ${completed.totalDurationMs}`,
    });
  }
});

export const HttpRuntimeTopRoutesSchema = z.object({
  byActivity: z.array(z.object({
    route: HttpRuntimeRoutePatternSchema,
    count: BoundedRuntimeIntegerSchema,
  }).strict()).max(ADMIN_RUNTIME_HTTP_TOP_ROUTE_COUNT),
  byErrors: z.array(z.object({
    route: HttpRuntimeRoutePatternSchema,
    errorCount: BoundedRuntimeIntegerSchema,
  }).strict()).max(ADMIN_RUNTIME_HTTP_TOP_ROUTE_COUNT),
  bySlowest: z.array(z.object({
    route: HttpRuntimeRoutePatternSchema,
    maxDurationMs: BoundedRuntimeIntegerSchema,
  }).strict()).max(ADMIN_RUNTIME_HTTP_TOP_ROUTE_COUNT),
}).strict();

/** One process-local HTTP pulse, as stored in the runtime telemetry sample. */
export const HttpRuntimeSnapshotSchema = z.object({
  inFlight: BoundedRuntimeIntegerSchema,
  received: BoundedRuntimeIntegerSchema,
  completed: HttpRuntimeCompletedSchema,
  topRoutes: HttpRuntimeTopRoutesSchema,
}).strict();

export const HttpRuntimeInstanceSchema = HttpRuntimeSnapshotSchema.extend({
  /** Anonymous boot-scoped process label, not a Redis or Render identifier. */
  label: z.string().min(1).max(128),
  latestPublishedAt: z.string().datetime({ offset: true }),
}).strict();

/** Fleet totals are exact; route rankings deliberately stay instance-local. */
export const HttpRuntimeAggregateSchema = HttpRuntimeSnapshotSchema.omit({ topRoutes: true }).extend({
  monitoredProcessCount: BoundedRuntimeIntegerSchema,
  instances: z.array(HttpRuntimeInstanceSchema),
}).strict();

export type HttpRuntimeStatusClass = (typeof ADMIN_HTTP_RUNTIME_STATUS_CLASSES)[number];
export type HttpRuntimeStatusClasses = z.infer<typeof HttpRuntimeStatusClassesSchema>;
export type HttpRouteActivity = z.infer<typeof HttpRuntimeTopRoutesSchema>['byActivity'][number];
export type HttpRouteErrors = z.infer<typeof HttpRuntimeTopRoutesSchema>['byErrors'][number];
export type HttpRouteSlowness = z.infer<typeof HttpRuntimeTopRoutesSchema>['bySlowest'][number];
export type HttpRuntimeSnapshot = z.infer<typeof HttpRuntimeSnapshotSchema>;
export type HttpRuntimeInstance = z.infer<typeof HttpRuntimeInstanceSchema>;
export type HttpRuntimeAggregate = z.infer<typeof HttpRuntimeAggregateSchema>;

/**
 * SQL query latency, mirroring the HTTP runtime contract above. There is no
 * per-route text: a label identifies `<Repository>.<method>` (or a table name
 * for BaseRepository's generic CRUD), never raw SQL — the driver text can
 * embed literal parameter values.
 */
export const ADMIN_RUNTIME_SQL_QUERY_LABEL_LIMIT = 96;
export const ADMIN_RUNTIME_SQL_TOP_SLOWEST_COUNT = 3;

export const SqlQueryLabelSchema = z.string()
  .min(1)
  .max(ADMIN_RUNTIME_SQL_QUERY_LABEL_LIMIT)
  .refine((label) => !/['";]/.test(label), 'SQL query label must not contain quote or statement-terminator characters')
  .refine((label) => !/\b(select|insert|update|delete)\b/i.test(label), 'SQL query label must not contain raw SQL text');

export const SqlQuerySlownessSchema = z.object({
  label: SqlQueryLabelSchema,
  count: BoundedRuntimeIntegerSchema,
  totalDurationMs: BoundedRuntimeIntegerSchema,
  maxDurationMs: BoundedRuntimeIntegerSchema,
}).strict();

const SqlQueryRuntimeSnapshotObjectSchema = z.object({
  count: BoundedRuntimeIntegerSchema,
  totalDurationMs: BoundedRuntimeIntegerSchema,
  maxDurationMs: BoundedRuntimeIntegerSchema,
  /** Queries past the slow-query threshold; a subset of `count`, never exceeds it. */
  slowCount: BoundedRuntimeIntegerSchema,
  topSlowest: z.array(SqlQuerySlownessSchema).max(ADMIN_RUNTIME_SQL_TOP_SLOWEST_COUNT),
}).strict();

function checkSqlQuerySnapshotInvariant(
  snapshot: { count: number; totalDurationMs: number; maxDurationMs: number; slowCount: number },
  context: z.RefinementCtx,
): void {
  if (snapshot.slowCount > snapshot.count || snapshot.maxDurationMs > snapshot.totalDurationMs) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `SQL query invariant failed: slowCount ${snapshot.slowCount} must not exceed count ${snapshot.count}; maxDurationMs ${snapshot.maxDurationMs} must not exceed totalDurationMs ${snapshot.totalDurationMs}`,
    });
  }
}

/** One process-local SQL pulse, as stored in the runtime telemetry sample. */
export const SqlQueryRuntimeSnapshotSchema = SqlQueryRuntimeSnapshotObjectSchema
  .superRefine(checkSqlQuerySnapshotInvariant);

export const SqlQueryRuntimeInstanceSchema = SqlQueryRuntimeSnapshotObjectSchema.extend({
  /** Anonymous boot-scoped process label, not a Redis or Render identifier. */
  label: z.string().min(1).max(128),
  latestPublishedAt: z.string().datetime({ offset: true }),
}).strict().superRefine(checkSqlQuerySnapshotInvariant);

/** Fleet totals are exact; the slowest-label ranking deliberately stays instance-local. */
export const SqlQueryRuntimeAggregateSchema = SqlQueryRuntimeSnapshotObjectSchema.omit({ topSlowest: true }).extend({
  monitoredProcessCount: BoundedRuntimeIntegerSchema,
  instances: z.array(SqlQueryRuntimeInstanceSchema),
}).strict().superRefine(checkSqlQuerySnapshotInvariant);

export type SqlQuerySlowness = z.infer<typeof SqlQuerySlownessSchema>;
export type SqlQueryRuntimeSnapshot = z.infer<typeof SqlQueryRuntimeSnapshotSchema>;
export type SqlQueryRuntimeInstance = z.infer<typeof SqlQueryRuntimeInstanceSchema>;
export type SqlQueryRuntimeAggregate = z.infer<typeof SqlQueryRuntimeAggregateSchema>;

/**
 * Long runtime series (#1800): the staff windows served by
 * `GET /api/admin/runtime-observability/series?hours=`. 48 h is also the Redis
 * retention of the compact key, so no window can ask for data already trimmed.
 */
export const RUNTIME_SERIES_WINDOW_HOURS = [48, 24, 7, 3] as const;
export const RUNTIME_SERIES_DEFAULT_WINDOW_HOURS = 48;
/** Upper bound on buckets per curve, so a 48 h chart stays readable and cheap to render. */
export const RUNTIME_SERIES_MAX_BUCKETS = 600;

export type RuntimeSeriesWindowHours = (typeof RUNTIME_SERIES_WINDOW_HOURS)[number];

/** Derived from the constant above: the list of admitted windows lives in one place. */
export const RuntimeSeriesWindowHoursSchema = z.number().refine(
  (hours): hours is RuntimeSeriesWindowHours => (RUNTIME_SERIES_WINDOW_HOURS as readonly number[]).includes(hours),
  { message: `runtime series window must be one of ${RUNTIME_SERIES_WINDOW_HOURS.join(', ')} hours` },
);

/**
 * Event-loop facts of one bucket. `utilizationRatio` is exact over the bucket
 * (sum of active time / sum of active + idle time); delays are the worst pulse
 * of the bucket because percentile summaries cannot be re-ranked.
 */
export const RuntimeSeriesEventLoopSchema = z.object({
  /**
   * Null in a bucket holding only a boot's first point: utilization is a
   * difference of since-boot totals and needs a previous point of that boot.
   */
  utilizationRatio: z.number().min(0).max(1).nullable(),
  /** Null when no pulse of the bucket completed a Node histogram sample. */
  delayP99Ms: BoundedRuntimeIntegerSchema.nullable(),
  delayMaxMs: BoundedRuntimeIntegerSchema.nullable(),
}).strict();

export const RuntimeSeriesRoleBucketSchema = z.object({
  /** Distinct boot ids that published in the bucket; a restart counts both boots. */
  processCount: BoundedRuntimeIntegerSchema,
  pool: z.object({
    /** Sum of the configured pool ceilings, null when no process had an initialized pool. */
    configuredMax: BoundedRuntimeIntegerSchema.nullable(),
    /** Sum over processes of each one's peak open connections within the bucket. */
    peakTotal: BoundedRuntimeIntegerSchema,
    /** Sum over processes of each one's peak waiting callers within the bucket. */
    peakWaiting: BoundedRuntimeIntegerSchema,
    /** Acquisitions that timed out during the bucket (not a since-boot total). */
    acquisitionTimeouts: BoundedRuntimeIntegerSchema,
    /** Acquisitions that succeeded on their single retry during the bucket. */
    acquisitionRetries: BoundedRuntimeIntegerSchema,
  }).strict(),
  /** Null when no process of the role published SQL counters in the bucket. */
  sql: z.object({
    count: BoundedRuntimeIntegerSchema,
    totalDurationMs: BoundedRuntimeIntegerSchema,
    maxDurationMs: BoundedRuntimeIntegerSchema,
    slowCount: BoundedRuntimeIntegerSchema,
  }).strict().nullable(),
  /** Backend only; the worker event loop is not measured. */
  eventLoop: RuntimeSeriesEventLoopSchema.nullable(),
}).strict();

export const RuntimeSeriesBucketSchema = z.object({
  /** Bucket start in UTC; buckets are aligned on epoch multiples of the step. */
  startAt: z.string().datetime({ offset: true }),
  /** Null means no process of that role published: a telemetry hole, never a zero. */
  backend: RuntimeSeriesRoleBucketSchema.nullable(),
  worker: RuntimeSeriesRoleBucketSchema.nullable(),
  /**
   * OVH PostgreSQL connections (peak) and ceiling. The sampler runs every
   * 60 s, so at 30 s steps every other bucket is legitimately null.
   */
  postgres: z.object({
    databaseConnections: BoundedRuntimeIntegerSchema,
    maxConnections: BoundedRuntimeIntegerSchema,
  }).strict().nullable(),
}).strict();

/** One backend boot: a restart is a new label, hence a new curve. */
export const RuntimeSeriesInstanceSchema = z.object({
  /** Anonymous boot-scoped label, same derivation as the other runtime views. */
  label: z.string().min(1).max(128),
  role: z.literal('backend'),
  /** First and last publication inside the window: where the curve starts and ends. */
  firstPublishedAt: z.string().datetime({ offset: true }),
  lastPublishedAt: z.string().datetime({ offset: true }),
  /** Aligned index-for-index with `buckets`; null where this boot did not publish. */
  eventLoop: z.array(RuntimeSeriesEventLoopSchema.nullable()).max(RUNTIME_SERIES_MAX_BUCKETS),
}).strict();

export const RuntimeSeriesSchema = z.object({
  observedAt: z.string().datetime({ offset: true }),
  windowHours: RuntimeSeriesWindowHoursSchema,
  stepSeconds: BoundedRuntimeIntegerSchema,
  buckets: z.array(RuntimeSeriesBucketSchema).max(RUNTIME_SERIES_MAX_BUCKETS),
  instances: z.array(RuntimeSeriesInstanceSchema),
}).strict();

export type RuntimeSeriesEventLoop = z.infer<typeof RuntimeSeriesEventLoopSchema>;
export type RuntimeSeriesRoleBucket = z.infer<typeof RuntimeSeriesRoleBucketSchema>;
export type RuntimeSeriesBucket = z.infer<typeof RuntimeSeriesBucketSchema>;
export type RuntimeSeriesInstance = z.infer<typeof RuntimeSeriesInstanceSchema>;
export type RuntimeSeries = z.infer<typeof RuntimeSeriesSchema>;
